import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, User, FileText, Database, Globe, Lock, Cookie, Users, Edit, Mail, AlertTriangle, Server, Clock, Scale, MessageSquare, MapPin, Wallet, Eye, Settings, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const WHITE_LOGO_URL = "https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/c4f0c6sm_YBSLogoWhiteNoBack.png";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/" className="p-2 rounded-xl hover:bg-secondary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
            <p className="text-muted-foreground text-sm mt-1">Your Buy Site • Last updated: April 2026</p>
          </div>
        </div>

        {/* Section 1: Introduction */}
        <div className="card p-6 mb-6 border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-blue-500/5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground mb-3">1. Introduction</h2>
              <p className="text-muted-foreground mb-3">
                Your Buy Site ("Your Buy Site", "we", "us", "our") respects your privacy and processes personal data in accordance with applicable data protection laws.
              </p>
              <p className="text-foreground font-medium">
                This Privacy Policy explains how we collect, use, and protect your data when using our platform.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Data We Collect */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">2. Data We Collect</h2>
          </div>
          <p className="text-muted-foreground mb-4">We may collect the following categories of data:</p>
          
          <div className="space-y-4">
            {/* Account Data */}
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-foreground">Account Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Name</li>
                <li>• Email address</li>
                <li>• Login credentials</li>
              </ul>
            </div>

            {/* Profile & Listing Data */}
            <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-green-500" />
                <h3 className="font-semibold text-foreground">Profile & Listing Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Listings, images, descriptions</li>
                <li>• Business information (if applicable)</li>
              </ul>
            </div>

            {/* Communication Data */}
            <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-cyan-500" />
                <h3 className="font-semibold text-foreground">Communication Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Messages exchanged through the platform</li>
              </ul>
            </div>

            {/* Technical Data */}
            <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Server className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-foreground">Technical Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• IP address</li>
                <li>• Device and browser information</li>
              </ul>
            </div>

            {/* Usage Data */}
            <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-foreground">Usage Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Click behavior</li>
                <li>• Search history</li>
                <li>• Preferences and interactions</li>
              </ul>
            </div>

            {/* Location Data */}
            <div className="p-4 bg-rose-500/10 rounded-xl border border-rose-500/20">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-rose-500" />
                <h3 className="font-semibold text-foreground">Location Data</h3>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Approximate location based on IP</li>
              </ul>
            </div>

            {/* Cryptocurrency Data */}
            <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-foreground">Cryptocurrency Data</h3>
                <span className="text-xs text-muted-foreground">(if shared by users)</span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground ml-7">
                <li>• Wallet addresses</li>
                <li>• Transaction references</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 3: Legal Basis for Processing */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">3. Legal Basis for Processing</h2>
          </div>
          <p className="text-muted-foreground mb-3">We process personal data based on:</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-3 bg-secondary rounded-xl">
              <p className="font-semibold text-foreground text-sm">Contractual necessity</p>
              <p className="text-xs text-muted-foreground">account & platform use</p>
            </div>
            <div className="p-3 bg-secondary rounded-xl">
              <p className="font-semibold text-foreground text-sm">Legitimate interest</p>
              <p className="text-xs text-muted-foreground">security, fraud prevention, platform improvement</p>
            </div>
            <div className="p-3 bg-secondary rounded-xl">
              <p className="font-semibold text-foreground text-sm">Consent</p>
              <p className="text-xs text-muted-foreground">marketing, cookies</p>
            </div>
            <div className="p-3 bg-secondary rounded-xl">
              <p className="font-semibold text-foreground text-sm">Legal obligations</p>
              <p className="text-xs text-muted-foreground">if applicable</p>
            </div>
          </div>
        </div>

        {/* Section 4: Purpose of Processing */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">4. Purpose of Processing</h2>
          </div>
          <p className="text-muted-foreground mb-3">We use data to:</p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-center gap-2">• Operate and maintain the platform</li>
            <li className="flex items-center gap-2">• Enable communication between users</li>
            <li className="flex items-center gap-2">• Detect and prevent fraud and abuse</li>
            <li className="flex items-center gap-2">• Improve platform performance and user experience</li>
            <li className="flex items-center gap-2">• Provide support and updates</li>
            <li className="flex items-center gap-2">• Promote listings and platform content</li>
          </ul>
        </div>

        {/* Section 5: User-to-User Data Responsibility */}
        <div className="card p-6 mb-6 border-2 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-bold text-foreground">5. User-to-User Data Responsibility</h2>
          </div>
          <p className="text-foreground font-medium mb-3">
            Your Buy Site acts only as a platform.
          </p>
          <p className="text-muted-foreground mb-2">When users share personal data with each other (e.g. via chat or listings):</p>
          <ul className="space-y-1 text-muted-foreground mb-3">
            <li>• This is done at their own risk</li>
            <li>• Users are independently responsible for such data sharing</li>
          </ul>
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <p className="text-foreground font-medium flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span>Your Buy Site is not responsible for misuse by other users.</span>
            </p>
          </div>
        </div>

        {/* Section 6: Data Retention */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">6. Data Retention</h2>
          </div>
          <p className="text-muted-foreground mb-2">We retain personal data:</p>
          <ul className="space-y-1 text-muted-foreground mb-4">
            <li>• As long as the account is active</li>
            <li>• Up to 12 months for logs and security data</li>
            <li>• Until consent is withdrawn (marketing)</li>
          </ul>
          <p className="text-foreground font-medium text-sm">
            We may retain data longer if required by law or for legal protection.
          </p>
        </div>

        {/* Section 7: Sharing Data with Third Parties */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">7. Sharing Data with Third Parties</h2>
          </div>
          <p className="text-muted-foreground mb-2">We may share data with:</p>
          <ul className="space-y-1 text-muted-foreground mb-4">
            <li>• Hosting and infrastructure providers</li>
            <li>• Analytics providers</li>
            <li>• Security and fraud prevention tools</li>
            <li>• Future payment or verification providers</li>
          </ul>
          <p className="text-foreground font-medium text-sm">
            All third parties must comply with applicable data protection laws.
          </p>
        </div>

        {/* Section 8: International Data Transfers */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">8. International Data Transfers</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            Data may be processed outside Curaçao or the European Union.
          </p>
          <p className="text-foreground font-medium">
            Where applicable, we ensure appropriate safeguards (e.g. contractual protections).
          </p>
        </div>

        {/* Section 9: Cookies */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">9. Cookies</h2>
          </div>
          <p className="text-muted-foreground mb-2">We use:</p>
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Functional cookies (required)
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Analytical cookies (performance & insights)
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              Marketing cookies (with consent)
            </div>
          </div>
          <p className="text-foreground font-medium text-sm">
            Users can manage cookies via browser settings or the platform cookie banner.
          </p>
        </div>

        {/* Section 10: Data Security */}
        <div className="card p-6 mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-2 border-amber-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-bold text-foreground">10. Data Security</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            We implement reasonable technical and organizational measures.
          </p>
          <p className="text-muted-foreground mb-2">However:</p>
          <ul className="space-y-1 text-muted-foreground mb-3">
            <li>• No system is 100% secure</li>
            <li>• We cannot guarantee absolute security</li>
          </ul>
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <p className="text-foreground font-medium flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span>Users share data at their own risk.</span>
            </p>
          </div>
        </div>

        {/* Section 11: User Rights */}
        <div className="card p-6 mb-6 border-2 border-blue-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-bold text-foreground">11. User Rights</h2>
          </div>
          <p className="text-muted-foreground mb-3">Users have the right to:</p>
          <div className="grid md:grid-cols-2 gap-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Access their data
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Correct inaccurate data
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Request deletion
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Restrict processing
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Object to processing
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Data portability
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Withdraw consent
            </div>
          </div>
          <p className="text-foreground font-medium text-sm mb-2">
            Requests can be submitted via email.
          </p>
          <p className="text-muted-foreground text-sm">
            We may request identity verification.
          </p>
        </div>

        {/* Section 12: Platform Data Usage Rights */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">12. Platform Data Usage Rights</h2>
          </div>
          <p className="text-muted-foreground mb-3">By using the platform, users acknowledge that:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Listings and content may be displayed publicly</li>
            <li>• Content may be used for marketing and promotion</li>
            <li>• Platform activity may be analyzed to improve services</li>
          </ul>
        </div>

        {/* Section 13: Limitation of Liability */}
        <div className="card p-6 mb-6 border-2 border-red-500/30 bg-gradient-to-r from-red-500/5 to-rose-500/5">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-foreground">13. Limitation of Liability (Privacy)</h2>
          </div>
          <p className="text-muted-foreground mb-3">To the maximum extent permitted by law:</p>
          <p className="text-foreground font-medium mb-2">Your Buy Site is not liable for:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Unauthorized access beyond our control</li>
            <li>• Data loss or breaches caused by third parties</li>
            <li>• User-to-user data misuse</li>
          </ul>
        </div>

        {/* Section 14: Changes */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">14. Changes</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            We may update this Privacy Policy at any time.
          </p>
          <p className="text-foreground font-medium">
            Significant changes will be communicated via the platform or email.
          </p>
        </div>

        {/* Section 15: Contact */}
        <div className="card p-6 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h2 className="text-xl font-bold text-foreground mb-2">15. Contact</h2>
          <p className="text-muted-foreground mb-4">
            For questions or requests regarding personal data:
          </p>
          <div className="p-4 bg-slate-800 rounded-xl inline-block">
            <img src={WHITE_LOGO_URL} alt="Your Buy Site" className="h-5 w-auto mx-auto mb-2" />
            <p className="text-white font-semibold">Your Buy Site</p>
            <p className="text-slate-300 text-sm">Curaçao, Caribbean Netherlands</p>
            <p className="text-cyan-400 text-sm mt-2">info@yourbuysite.com</p>
          </div>
        </div>

        {/* Agreement Notice */}
        <div className="mt-8 p-4 bg-secondary rounded-xl text-center">
          <p className="text-sm text-muted-foreground">
            By using Your Buy Site, you acknowledge that you have read, understood, and agree to this 
            Privacy Policy. If you do not agree, please do not use our platform.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
